/**
 * Environment Configuration (Development)
 * 
 * Loads environment variables from Vite at build time and validates configuration.
 * Supports development, test, and production environments through different .env files.
 * 
 * Required Environment Variables:
 * - VITE_API_URL: Backend API base URL
 * - VITE_ENV_NAME: Environment name (development, test, production)
 * - VITE_AZURE_CLIENT_ID: Azure AD application ID
 * - VITE_AZURE_AUTHORITY: Azure AD tenant authority URL
 * - VITE_AZURE_REDIRECT_URI: OAuth redirect URI
 * - VITE_AZURE_SCOPES: Comma-separated API scopes
 * 
 * Optional Environment Variables:
 * - VITE_BACKEND_TIMEOUT: Backend request timeout in ms (default: 120000)
 * - VITE_DEV_AUTH_OFF: Disable authentication in development (default: false)
 */

import { Environment, validateEnvironment } from '../types/environment.types';

/**
 * Environment Configuration Object
 * 
 * Populated from Vite environment variables at build time.
 * Configuration is baked into the build and cannot be changed at runtime.
 * 
 * @type {Environment}
 * @property {boolean} production - Whether running in production mode
 * @property {string} apiUrl - Backend API base URL
 * @property {string} name - Environment name (development, test, production)
 * @property {number} backendTimeout - Backend request timeout in milliseconds
 * @property {boolean} devAuthOff - Whether authentication is disabled in development
 * @property {Object} azure - Azure AD configuration
 * @property {string} azure.clientId - Azure AD application ID
 * @property {string} azure.authority - Azure AD tenant authority URL
 * @property {string} azure.redirectUri - OAuth redirect URI after login
 * @property {string[]} azure.scopes - Array of API access scopes
 */
export const environment: Environment = {
  production: import.meta.env.PROD,
  apiUrl: import.meta.env.VITE_API_URL,
  name: import.meta.env.VITE_ENV_NAME,
  backendTimeout: import.meta.env.VITE_BACKEND_TIMEOUT ? parseInt(import.meta.env.VITE_BACKEND_TIMEOUT) : 120000,
  devAuthOff: import.meta.env.VITE_DEV_AUTH_OFF === 'true',
  azure: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID,
    authority: import.meta.env.VITE_AZURE_AUTHORITY,
    redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI,
    scopes: import.meta.env.VITE_AZURE_SCOPES.split(',')
  }
};

validateEnvironment(environment);
